// src/artist/ArtWorks.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import artworkService from '../../services/artworkService';
import titService from '../../services/titService';
import contractService from '../../services/contractService';
import { useToast } from '../../hooks/useToast';
import { extractApiList } from '../../utils/responseAdapters';
import DataCardList from '../../components/common/DataCardList';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import '../../styles/FormModal.css';
import '../../styles/Artworks.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDebian } from '@fortawesome/free-brands-svg-icons';
import {
  faDeleteLeft,
  faPenToSquare,
  faRupeeSign,
  faShieldHalved,
  faCloudArrowUp,
  faGem,
  faTag,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { resolveImageUrl, onImageError } from '../../utils/imageUrl';

// ─── Add Artwork Modal ──────────────────────────────────────────────────────────
const AddArtworkModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    file: null,
    title: '',
    description: '',
    price: '',
    size: '',
    type: '',
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, file }));
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, file: null }));
    setPreviewUrl(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Artwork"
      subtitle="Catalog and upload a fine art masterpiece to your gallery"
      maxWidth={580}
      actions={
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} style={{ flex: 1 }}>
            Upload Artwork
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        {/* Artwork Image Dropzone */}
        <div className="form-group">
          <label>Artwork Image *</label>
          <div className="image-dropzone">
            <input
              type="file"
              id="file"
              name="file"
              onChange={handleFileChange}
              required={!previewUrl}
              accept="image/*"
            />
            {previewUrl ? (
              <div className="dropzone-preview-wrap">
                <img src={previewUrl} alt="Preview" className="dropzone-preview-img" />
                <button
                  type="button"
                  className="dropzone-remove-btn"
                  onClick={handleRemoveFile}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="dropzone-prompt">
                <FontAwesomeIcon icon={faCloudArrowUp} className="dropzone-icon" />
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                  Click or drag artwork image here
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Supports PNG, JPG, JPEG, WEBP up to 25MB
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Title & Price */}
        <div className="form-grid-2col">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Artwork title"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="price">Price (NPR)</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              placeholder="e.g. 50000"
              min="0"
            />
          </div>
        </div>

        {/* Size & Medium/Type */}
        <div className="form-grid-2col">
          <div className="form-group">
            <label htmlFor="size">Dimensions / Size</label>
            <input
              type="text"
              id="size"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
              placeholder="e.g. 36x48 inches, Canvas"
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Medium / Type</label>
            <input
              type="text"
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              placeholder="e.g. Oil on Canvas, Sculpture"
            />
          </div>
        </div>

        {/* Description */}
        <div className="form-group">
          <label htmlFor="description">Curatorial Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
            placeholder="Artistic context, materials, provenance, or story..."
          />
        </div>
      </form>
    </Modal>
  );
};

// ─── Edit Artwork Modal ─────────────────────────────────────────────────────────
const EditArtworkModal = ({ isOpen, onClose, onSubmit, artwork }) => {
  const [formData, setFormData] = useState({
    file: null,
    title: '',
    description: '',
    price: '',
    size: '',
    type: '',
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (artwork && isOpen) {
      setFormData({
        file: null,
        title: artwork.title || '',
        description: artwork.description || '',
        price: artwork.price || '',
        size: artwork.size || '',
        type: artwork.type || '',
      });
      setPreviewUrl(artwork.image_url ? resolveImageUrl(artwork.image_url) : null);
    }
  }, [artwork, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, file }));
    const reader = new FileReader();
    reader.onloadend = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, artwork.art_id || artwork._id || artwork.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Artwork"
      subtitle="Update masterpiece metadata, pricing, or catalog details"
      maxWidth={580}
      actions={
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} style={{ flex: 1 }}>
            Save Changes
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-group">
          <label>Artwork Image (Optional replace)</label>
          <div className="image-dropzone">
            <input type="file" accept="image/*" onChange={handleFileChange} />
            {previewUrl ? (
              <div className="dropzone-preview-wrap">
                <img src={previewUrl} alt="Preview" className="dropzone-preview-img" />
              </div>
            ) : (
              <div className="dropzone-prompt">
                <FontAwesomeIcon icon={faCloudArrowUp} className="dropzone-icon" />
                <span>Upload replacement image</span>
              </div>
            )}
          </div>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Price (NPR)</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleInputChange}
              min="0"
            />
          </div>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Dimensions / Size</label>
            <input
              type="text"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Type / Medium</label>
            <input
              type="text"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="3"
          />
        </div>
      </form>
    </Modal>
  );
};

// ─── TitTat Application Modal ──────────────────────────────────────────────────
const TitApplicationModal = ({ isOpen, onClose, onSubmit, artwork }) => {
  const [formData, setFormData] = useState({
    artist_name: '',
    price: '',
    definition: '',
    time_taken: '',
    token_unit: '4',
    rarity: '5',
    artist_reputation: 'Established',
    instagram_link: '',
    facebook_link: '',
    twitter_link: '',
  });

  useEffect(() => {
    if (artwork && isOpen) {
      setFormData((prev) => ({
        ...prev,
        artist_name: artwork.artist_name || '',
        price: artwork.price || '',
        definition: artwork.description || '',
      }));
    }
  }, [artwork, isOpen]);

  if (!isOpen || !artwork) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, artwork.art_id || artwork._id || artwork.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Apply for TitTat Tokenization"
      subtitle={`Convert "${artwork.title || 'Artwork'}" into fractionalized digital TitTat asset units`}
      maxWidth={640}
      actions={
        <div style={{ display: 'flex', gap: '0.75rem', width: '100%' }}>
          <Button type="button" variant="secondary" onClick={onClose} style={{ flex: 1 }}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            style={{ flex: 1 }}
          >
            💎 Submit TitTat Application
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="form-warning">
          ⚠️ <strong>Irreversible Tokenization Protocol:</strong> Once an artwork is approved and
          fractionalized into TitTats, it enters institutional smart custody and cannot be reverted
          back to traditional sole ownership.
        </div>

        {/* Section 1: Token Economics */}
        <div className="form-section-divider">
          <span className="form-section-title">
            <FontAwesomeIcon icon={faGem} /> 1. Fractional Token Economics
          </span>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Fraction Base Valuation (NPR) *</label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="e.g. 500000"
              required
            />
          </div>

          <div className="form-group">
            <label>Fraction Units (Max 6) *</label>
            <input
              type="number"
              name="token_unit"
              min="1"
              max="6"
              value={formData.token_unit}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Rarity Tier (1–10) *</label>
            <input
              type="number"
              name="rarity"
              min="1"
              max="10"
              value={formData.rarity}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Creation Duration (Days) *</label>
            <input
              type="number"
              name="time_taken"
              value={formData.time_taken}
              onChange={handleChange}
              placeholder="e.g. 45"
              required
            />
          </div>
        </div>

        {/* Section 2: Creator Identity & Definition */}
        <div className="form-section-divider">
          <span className="form-section-title">
            <FontAwesomeIcon icon={faShieldHalved} /> 2. Creator Identity &amp; Manifesto
          </span>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Verified Artist Name *</label>
            <input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleChange}
              placeholder="Artist legal or signature name"
              required
            />
          </div>

          <div className="form-group">
            <label>Artist Reputation Tier</label>
            <input
              type="text"
              name="artist_reputation"
              value={formData.artist_reputation}
              onChange={handleChange}
              placeholder="e.g. Master, Emerging, Blue-Chip"
            />
          </div>
        </div>

        <div className="form-group">
          <label>TitTat Definition / Curatorial Manifesto *</label>
          <textarea
            name="definition"
            value={formData.definition}
            onChange={handleChange}
            rows="3"
            placeholder="Detailed thematic significance, conceptual foundation, and provenance..."
            required
          />
        </div>

        {/* Section 3: Social & Provenance Verification */}
        <div className="form-section-divider">
          <span className="form-section-title">
            <FontAwesomeIcon icon={faCircleCheck} /> 3. Verification &amp; Social Proof
          </span>
        </div>

        <div className="form-grid-2col">
          <div className="form-group">
            <label>Instagram Handle / Link</label>
            <input
              type="text"
              name="instagram_link"
              value={formData.instagram_link}
              onChange={handleChange}
              placeholder="https://instagram.com/..."
            />
          </div>

          <div className="form-group">
            <label>X / Twitter Handle / Link</label>
            <input
              type="text"
              name="twitter_link"
              value={formData.twitter_link}
              onChange={handleChange}
              placeholder="https://x.com/..."
            />
          </div>
        </div>

        <div className="form-group">
          <label>Facebook / Portfolio Portfolio Link</label>
          <input
            type="text"
            name="facebook_link"
            value={formData.facebook_link}
            onChange={handleChange}
            placeholder="https://portfolio..."
          />
        </div>
      </form>
    </Modal>
  );
};

// ─── Main Artist ArtWorks Page ────────────────────────────────────────────────
export default function ArtWorks() {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTitModalOpen, setIsTitModalOpen] = useState(false);
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDetailView, setIsDetailView] = useState(false);
  const [salePrice, setSalePrice] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [hasActiveContract, setHasActiveContract] = useState(false);

  // Check active management contract on mount
  useEffect(() => {
    contractService
      .getMyContractsArtist('active')
      .then((res) => {
        const contracts = extractApiList(res?.data);
        setHasActiveContract(Array.isArray(contracts) && contracts.length > 0);
      })
      .catch(() => setHasActiveContract(false));
  }, []);

  const statusOptions = ['available', 'unavailable', 'sold', 'reserved'];

  const fetchArtworks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await artworkService.getArtworks();
      const normalized = extractApiList(response?.data);
      setArtworks(normalized);
    } catch (error) {
      console.error('Failed to fetch artworks', error);
      showToast('Failed to load artworks', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchArtworks();
  }, [fetchArtworks]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleEditArtwork = async (formData, artId) => {
    try {
      const data = new FormData();
      if (formData.file) data.append('file', formData.file);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('price', formData.price);
      data.append('size', formData.size);
      data.append('type', formData.type);

      await artworkService.updateArtwork(artId, data);
      setIsEditModalOpen(false);
      fetchArtworks();
      showToast('Artwork updated successfully', 200);
    } catch (error) {
      console.error('Failed to update artwork', error);
      showToast('Failed to update artwork', 500);
    }
  };

  const handleEdit = (artwork) => {
    setSelectedArtwork(artwork);
    setIsEditModalOpen(true);
  };

  const handleTit = (artwork) => {
    if (!hasActiveContract) {
      showToast(
        'You need an active management contract to apply for TitTat. Go to Contracts page.',
        403
      );
      navigate('/artist/dashboard/contracts');
      return;
    }
    setSelectedArtwork(artwork);
    setIsTitModalOpen(true);
  };

  const handleTitArtwork = async (formData, artId) => {
    try {
      await titService.applyTokenization(artId, formData);
      setIsTitModalOpen(false);
      fetchArtworks();
      showToast('Tokenization application submitted', 200);
    } catch (error) {
      console.error('Tokenization failed', error);
      showToast('Tokenization application failed', 500);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this artwork?')) return;
    try {
      await artworkService.deleteArtwork(id);
      showToast('Artwork deleted successfully', 200);
      setArtworks((prev) =>
        prev.filter((artwork) => (artwork.id || artwork.art_id || artwork._id || artwork.artId) !== id)
      );
      if (isDetailView) {
        if (artworks.length > 1) {
          goToNext();
        } else {
          setIsDetailView(false);
        }
      }
    } catch (error) {
      console.error('Failed to delete artwork', error);
      showToast('Failed to delete artwork', 500);
    }
  };

  const handleAddArtwork = async (formData) => {
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('title', formData.title);
      data.append('description', formData.description);
      data.append('price', formData.price);
      data.append('size', formData.size);
      data.append('type', formData.type);

      await artworkService.uploadArtwork(data);
      setIsModalOpen(false);
      fetchArtworks();
      showToast('Artwork uploaded successfully', 200);
    } catch (error) {
      console.error('Failed to upload artwork', error);
      showToast('Failed to upload artwork', 500);
    }
  };

  const handlePutOnSale = async (artworkId) => {
    if (!salePrice || isNaN(salePrice) || parseFloat(salePrice) <= 0) {
      showToast('Please enter a valid price', 400);
      return;
    }

    try {
      await artworkService.putArtOnSale(artworkId, parseFloat(salePrice));
      setArtworks((prev) => {
        const updated = [...prev];
        if (updated[currentIndex]) {
          updated[currentIndex] = {
            ...updated[currentIndex],
            status: 'for_selling',
            price: parseFloat(salePrice),
          };
        }
        return updated;
      });
      setSalePrice('');
      fetchArtworks();
      showToast('Artwork listed for sale', 200);
    } catch (error) {
      console.error('Failed to put artwork on sale', error);
      showToast('Failed to put artwork on sale', 500);
    }
  };

  const handleWithdrawFromSale = async (artworkId) => {
    try {
      await artworkService.withdrawArtFromSale(artworkId);
      setArtworks((prev) => {
        const updated = [...prev];
        if (updated[currentIndex]) {
          updated[currentIndex] = {
            ...updated[currentIndex],
            status: 'available',
            price: undefined,
          };
        }
        return updated;
      });
      fetchArtworks();
      showToast('Artwork withdrawn from sale', 200);
    } catch (error) {
      console.error('Failed to withdraw artwork from sale', error);
      showToast('Failed to withdraw artwork from sale', 500);
    }
  };

  const handleStatusChange = async (artworkId, newStatus) => {
    try {
      await artworkService.changeArtworkStatus(artworkId, newStatus);
      setArtworks((prev) => {
        const updated = [...prev];
        if (updated[currentIndex]) {
          updated[currentIndex] = {
            ...updated[currentIndex],
            status: newStatus,
          };
        }
        return updated;
      });
      setShowStatusDropdown(false);
      fetchArtworks();
      showToast('Artwork status updated', 200);
    } catch (error) {
      console.error('Failed to update artwork status', error);
      showToast('Failed to update artwork status', 500);
    }
  };

  const toggleDescription = (id) => {
    setExpandedCardId((prev) => (prev === id ? null : id));
  };

  const getId = (artwork) => artwork?.art_id || artwork?.id || artwork?._id;

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : artworks.length - 1));
  }, [artworks.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < artworks.length - 1 ? prev + 1 : 0));
  }, [artworks.length]);

  const handleCardClick = (index) => {
    setCurrentIndex(index);
    setIsDetailView(true);
  };

  const closeDetailView = () => {
    setIsDetailView(false);
  };

  const renderArtworkContent = (artwork, index) => {
    const id = getId(artwork);
    const isExpanded = expandedCardId === id;
    const isCurrent = isDetailView && index === currentIndex;

    return (
      <>
        <div className={`artwork-image ${isCurrent ? 'detail-view' : ''}`}>
          <img
            src={resolveImageUrl(artwork.image_url)}
            alt={artwork.title || 'Artwork'}
            onError={onImageError}
            loading="lazy"
          />
        </div>

        <div className="artwork-card-body">
          <h3 className={`artwork-title ${isCurrent ? 'detail-view' : ''}`}>
            {artwork.title || 'Untitled Artwork'}
          </h3>

          <div className={`artwork-description-wrapper ${isCurrent ? 'detail-view' : ''}`}>
            <p
              className={`artwork-description ${
                isCurrent || isExpanded ? 'expanded' : 'collapsed'
              } ${isCurrent ? 'detail-view' : ''}`}
            >
              {artwork.description || 'No description provided.'}
            </p>
            {artwork.description && artwork.description.length > 50 && !isCurrent && (
              <button
                type="button"
                className="toggle-description-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleDescription(id);
                }}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>

          <div className={`artwork-meta ${isCurrent ? 'detail-view' : ''}`}>
            <div className="artwork-badge-row">
              {artwork.type && (
                <span className="artwork-badge badge-type">{artwork.type}</span>
              )}
              {artwork.status && (
                <span className={`artwork-badge badge-status status-${artwork.status}`}>
                  {artwork.status === 'tokenized' && '🔮 '}
                  {artwork.status === 'for_selling' && '🏷️ '}
                  {artwork.status === 'available' && '🟢 '}
                  {artwork.status.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            <div className="artwork-price-row">
              <span className="artwork-price">
                <FontAwesomeIcon icon={faRupeeSign} style={{ fontSize: '0.9rem' }} />{' '}
                {artwork.price ? Number(artwork.price).toLocaleString('en-IN') : 'Unlisted'}
              </span>
            </div>
          </div>
        </div>

        {isCurrent && (
          <div className="artwork-detail-actions">
            <div className="status-dropdown-container" ref={dropdownRef}>
              <button
                type="button"
                className="status-dropdown-btn"
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                aria-label="Change status"
              >
                <span className="dropdown-icon">⋮</span>
              </button>

              {showStatusDropdown && (
                <div className="status-dropdown-menu">
                  {statusOptions.map((status) => (
                    <button
                      type="button"
                      key={status}
                      className={`status-option ${artwork.status === status ? 'active' : ''}`}
                      onClick={() => handleStatusChange(id, status)}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="artwork-status-actions">
              {artwork.status === 'available' && (
                <div className="put-on-sale-form">
                  <input
                    type="number"
                    placeholder="Enter price in NPR"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    min="0"
                    step="100"
                  />
                  <Button variant="secondary" onClick={() => handlePutOnSale(id)}>
                    Put on Sale
                  </Button>
                </div>
              )}
              {artwork.status === 'for_selling' && (
                <Button variant="secondary" onClick={() => handleWithdrawFromSale(id)}>
                  Withdraw from Sale
                </Button>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <DashboardLayout>
      <div className="artworks-header">
        <Button variant="primary" onClick={() => setIsModalOpen(true)}>
          + Add New Artwork
        </Button>
      </div>

      {isDetailView ? (
        <div className="artwork-detail-container">
          <button className="nav-button prev" onClick={goToPrevious} aria-label="Previous artwork">
            &lt;
          </button>

          <div className="artwork-detail-card">
            {renderArtworkContent(artworks[currentIndex], currentIndex)}
            <div className="card-actions" style={{ padding: '12px 20px' }}>
              <div className="card-actions-custom">
                <div className="card-actions-left">
                  {artworks[currentIndex]?.status === 'tokenized' ? (
                    <span className="tittat-minted-badge">🔮 TitTat Minted</span>
                  ) : (
                    <button
                      type="button"
                      className="tittat-action-pill"
                      onClick={() => handleTit(artworks[currentIndex])}
                      title="Apply for TitTat tokenization"
                    >
                      <FontAwesomeIcon icon={faShieldHalved} /> Apply for TitTat
                    </button>
                  )}
                </div>

                <div className="card-actions-right">
                  <button
                    type="button"
                    className="card-action-btn edit-btn"
                    onClick={() => handleEdit(artworks[currentIndex])}
                    title="Edit artwork"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  <button
                    type="button"
                    className="card-action-btn delete-btn"
                    onClick={() => handleDelete(getId(artworks[currentIndex]))}
                    title="Delete artwork"
                  >
                    <FontAwesomeIcon icon={faDeleteLeft} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <button className="nav-button next" onClick={goToNext} aria-label="Next artwork">
            &gt;
          </button>

          <button
            type="button"
            className="close-detail"
            onClick={closeDetailView}
            aria-label="Close detail view"
          >
            ✕
          </button>
        </div>
      ) : (
        <DataCardList
          data={artworks}
          loading={loading}
          emptyMessage="No artworks cataloged in your gallery yet. Click '+ Add New Artwork' above to begin."
          renderContent={(artwork, index) => (
            <div
              onClick={() => handleCardClick(index)}
              style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', height: '100%' }}
            >
              {renderArtworkContent(artwork, index)}
            </div>
          )}
          renderActions={(artwork, index) => {
            const id = getId(artwork);
            return (
              <div className="card-actions-custom">
                <div className="card-actions-left">
                  {artwork.status === 'tokenized' ? (
                    <span className="tittat-minted-badge">🔮 TitTat Minted</span>
                  ) : (
                    <button
                      type="button"
                      className="tittat-action-pill"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTit(artwork);
                      }}
                      title="Apply for TitTat tokenization"
                    >
                      <FontAwesomeIcon icon={faShieldHalved} /> Apply for TitTat
                    </button>
                  )}
                </div>

                <div className="card-actions-right">
                  <button
                    type="button"
                    className="card-action-btn edit-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(artwork);
                    }}
                    title="Edit artwork"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </button>
                  <button
                    type="button"
                    className="card-action-btn delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(id);
                    }}
                    title="Delete artwork"
                  >
                    <FontAwesomeIcon icon={faDeleteLeft} />
                  </button>
                </div>
              </div>
            );
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <AddArtworkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddArtwork}
      />
      <EditArtworkModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditArtwork}
        artwork={selectedArtwork}
      />
      <TitApplicationModal
        isOpen={isTitModalOpen}
        onClose={() => setIsTitModalOpen(false)}
        onSubmit={handleTitArtwork}
        artwork={selectedArtwork}
      />
    </DashboardLayout>
  );
}